import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { pick, precisionOf, starsOf, useCountdown, TimerBar, Hud, BigButton, Flash, PRACTICE_SECONDS, FLASH_MS } from "./nqShared";

const TARGET = "🐉";
const CFG = {
  1: { seconds: 45, every: 1500, show: 1100, pTarget: 0.4, distractors: ["🦄", "🐢", "🦋", "🐙"] },
  2: { seconds: 60, every: 1300, show: 950, pTarget: 0.25, distractors: ["🐲", "🦖", "🐢", "🦎", "🐊"] },
  3: { seconds: 80, every: 1100, show: 800, pTarget: 0.15, distractors: ["🐲", "🦖", "🐊", "🦎", "🐸", "🟢"] },
};

export const brief = (level) => {
  const c = CFG[level.n];
  return {
    title: "Surveille l'horizon depuis la tour",
    goal: `attraper tous les dragons ${TARGET} qui passent sur le radar.`,
    regarde: "Des animaux apparaissent un par un au milieu du radar, puis disparaissent vite.",
    fais: `Appuie sur 📡 RADAR ! dès que tu vois le dragon vert ${TARGET} (ou sur la barre d'espace).`,
    attention: `Si ce n'est pas le dragon, ne touche à rien !${level.n > 1 ? " Certains animaux lui ressemblent beaucoup." : ""} La mission dure ${c.seconds} secondes : reste en alerte jusqu'au bout.`,
    preview: { ok: [TARGET], ko: c.distractors },
  };
};

export default function GameRadar({ power, level, onFinish, practice, onPracticeEnd }) {
  const cfg = useMemo(() => (practice ? { ...CFG[level.n], seconds: PRACTICE_SECONDS } : CFG[level.n]), [level, practice]);
  const [current, setCurrent] = useState(null);
  const [stats, setStats] = useState({ hits: 0, miss: 0, false: 0, targets: 0 });
  const [flash, setFlash] = useState(null);
  const answered = useRef(false);
  const running = true;

  const finish = useCallback(() => {
    if (practice) return onPracticeEnd();
    const precision = precisionOf(stats.hits, stats.targets, stats.false);
    return onFinish({ hits: stats.hits, total: stats.targets, errors: stats.false, precision, stars: starsOf(precision), detail: `${stats.hits}/${stats.targets} dragons attrapés · ${stats.miss} manqué(s) · ${stats.false} fausse(s) alerte(s)` });
  }, [stats, onFinish, practice, onPracticeEnd]);
  const left = useCountdown(cfg.seconds, running, finish);

  useEffect(() => {
    const tick = () => {
      const isTarget = Math.random() < cfg.pTarget;
      answered.current = false;
      const emoji = isTarget ? TARGET : pick(cfg.distractors);
      setCurrent({ emoji, isTarget, key: Date.now() });
      if (isTarget) setStats((s) => ({ ...s, targets: s.targets + 1 }));
      setTimeout(() => {
        setCurrent(null);
        if (isTarget && !answered.current) setStats((s) => ({ ...s, miss: s.miss + 1 }));
      }, cfg.show);
    };
    tick();
    const id = setInterval(tick, cfg.every);
    return () => clearInterval(id);
  }, [cfg]);

  const press = () => {
    if (answered.current) return;
    answered.current = true;
    if (current && current.isTarget) { setStats((s) => ({ ...s, hits: s.hits + 1 })); setFlash("ok"); }
    else { setStats((s) => ({ ...s, false: s.false + 1 })); setFlash("ko"); }
    setTimeout(() => setFlash(null), FLASH_MS);
  };

  return (
    <div className="space-y-4" data-testid="nq-game-radar">
      <TimerBar left={left} total={cfg.seconds} color={power.color} />
      <Hud items={[["Attrapés", stats.hits, "#7CC6A6"], ["Manqués", stats.miss, "#F2B84B"], ["Fausses alertes", stats.false, "#E0705C"]]} />
      <div className="relative rounded-3xl bg-[#ECE6F7] p-6 flex flex-col items-center gap-6">
        <Flash kind={flash} koLabel="✗ Pas le dragon !" />
        <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-full bg-white border-4 flex items-center justify-center text-7xl sm:text-8xl shadow-inner" style={{ borderColor: power.color }} data-testid="nq-radar-screen">
          {current ? <span key={current.key} className="animate-fade-up" data-testid={current.isTarget ? "nq-radar-target" : "nq-radar-distractor"}>{current.emoji}</span> : <span className="text-3xl text-[var(--beige)]">·</span>}
        </div>
        <BigButton onClick={press} color={power.color} testid="nq-radar-button">📡 RADAR !</BigButton>
        <p className="text-xs text-[var(--ink-soft)]">Astuce : tu peux aussi appuyer sur la barre d'espace.</p>
      </div>
      <KeyPress onPress={press} />
    </div>
  );
}

const KeyPress = ({ onPress }) => {
  useEffect(() => { const h = (e) => { if (e.code === "Space") { e.preventDefault(); onPress(); } }; window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onPress]);
  return null;
};
